import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { AddColumnDialog } from "@/features/data-curation/components/AddColumnDialog";
import { AgentPromptBox } from "@/features/data-curation/components/AgentPromptBox";
import {
  AgentTimer,
  type AgentTime,
} from "@/features/data-curation/components/AgentTimer";
import { DatasetShareButton } from "@/features/data-curation/components/DatasetShareButton";
import type {
  ExtractionCheckpoint,
  ExtractionCollectionPlan,
  ExtractionDataset,
  ExtractionGridActivity,
} from "@/features/data-curation/types";
import {
  Activity,
  BookmarkPlus,
  CalendarClock,
  ChevronRight,
  Download,
  Ellipsis,
  ExternalLink,
  History,
  Images,
  ListChecks,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const MAX_CHECKPOINTS = 10;

const formatDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

type Panel = "plan" | "activity" | "checkpoints" | "add-column";

interface CurationHeaderProps {
  dataset: ExtractionDataset;
  status: { label: string; variant: "blue" | "green" | "default" };
  agentTime: AgentTime | null;
  agentWorking: boolean;
  hasImageColumns: boolean;
  /** Show only the icons of the top buttons, as when the chat panel takes part of the page. */
  iconsOnly?: boolean;
  plan: ExtractionCollectionPlan | null | undefined;
  activity: ExtractionGridActivity[] | undefined;
  checkpoints: ExtractionCheckpoint[] | undefined;
  checkpointsLoading: boolean;
  checkpointsError: string | null;
  savingCheckpoint: boolean;
  deleting: boolean;
  csvPending: boolean;
  imagesPending: boolean;
  checkpointCsvId: string | null;
  checkpointImagesId: string | null;
  restoringCheckpointId: string | null;
  onSaveCheckpoint: () => void;
  onDownloadCsv: () => void;
  onDownloadImages: () => void;
  onDelete: () => void;
  onDownloadCheckpointCsv: (checkpoint: ExtractionCheckpoint) => void;
  onDownloadCheckpointImages: (checkpoint: ExtractionCheckpoint) => void;
  onRestoreCheckpoint: (checkpoint: ExtractionCheckpoint) => void;
  /** Opens the Monitor dialog without a selection; set while monitors can be added. */
  onNewMonitor?: (() => void) | null;
}

type ToolbarLabels = Record<"save" | "csv" | "images" | "share" | "monitor", string>;

/**
 * A toolbar button shows its label only when the toolbar's own container has
 * room for the labels up to that button, so the labels go one by one from the
 * right as the toolbar narrows, and the buttons stay on the line of the title.
 * The toolbar gets the room that the title does not use. The widths assume the
 * labels in 14px text, with some room to spare.
 */
const TOOLBAR_LABELS: ToolbarLabels = {
  save: "sr-only @min-[24.5rem]:not-sr-only",
  csv: "sr-only @min-[31.5rem]:not-sr-only",
  images: "sr-only",
  share: "sr-only @min-[35rem]:not-sr-only",
  monitor: "sr-only @min-[41rem]:not-sr-only",
};
const IMAGE_TOOLBAR_LABELS: ToolbarLabels = {
  save: "sr-only @min-[28rem]:not-sr-only",
  csv: "sr-only @min-[35rem]:not-sr-only",
  images: "sr-only @min-[43.5rem]:not-sr-only",
  share: "sr-only @min-[46.5rem]:not-sr-only",
  monitor: "sr-only @min-[52.5rem]:not-sr-only",
};
const ICON_LABELS: ToolbarLabels = {
  save: "sr-only",
  csv: "sr-only",
  images: "sr-only",
  share: "sr-only",
  monitor: "sr-only",
};

/** One compact block above the grid: the curation, its downloads, and the rest in a menu. */
export const CurationHeader = (props: CurationHeaderProps) => {
  const { dataset } = props;
  const working = dataset.status !== "draft";
  const labels = props.iconsOnly
    ? ICON_LABELS
    : props.hasImageColumns
      ? IMAGE_TOOLBAR_LABELS
      : TOOLBAR_LABELS;
  const [panel, setPanel] = useState<Panel | null>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const activityCount = props.activity?.length ?? 0;
  const checkpointCount = props.checkpoints?.length ?? 0;

  const closePanel = (open: boolean) => {
    if (!open) setPanel(null);
  };
  const returnFocus = (event: Event) => {
    event.preventDefault();
    moreRef.current?.focus();
  };

  return (
    <div className="@container rounded-[3px] border border-zinc-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 @md:flex-nowrap">
        <div className="flex min-w-0 items-center gap-2">
          <h2
            className="min-w-0 truncate text-lg font-semibold leading-7 text-zinc-900"
            title={dataset.name}
          >
            {dataset.name}
          </h2>
          <Badge className="shrink-0" variant={props.status.variant}>
            {props.status.label}
          </Badge>
          {dataset.limited_view && (
            <Badge
              className="shrink-0"
              title="This dataset shows the view of accounts outside GXL: their row limit and map options. The Balanced (limited) mode set it."
            >
              Limited view
            </Badge>
          )}
          {props.agentTime && (
            <span className="shrink-0">
              <AgentTimer {...props.agentTime} />
            </span>
          )}
        </div>
        <div className="@container flex min-w-[17.5rem] flex-1 items-center justify-end gap-2">
          {working && (
            <Tooltip content="Save a version of the dataset that you can download or restore later.">
              <Button
                size="sm"
                variant="outline"
                loading={props.savingCheckpoint}
                aria-label={props.savingCheckpoint ? "Saving checkpoint" : "Save checkpoint"}
                onClick={props.onSaveCheckpoint}
              >
                <BookmarkPlus className="h-4 w-4" aria-hidden />
                <span className={labels.save}>Save</span>
              </Button>
            </Tooltip>
          )}
          {working && (
            <Tooltip content="Download the current rows as CSV. No checkpoint needed.">
              <Button
                size="sm"
                variant="outline"
                loading={props.csvPending}
                aria-label={props.csvPending ? "Downloading extraction CSV" : undefined}
                onClick={props.onDownloadCsv}
              >
                <Download className="h-4 w-4" aria-hidden />
                <span className={labels.csv}>Download CSV</span>
              </Button>
            </Tooltip>
          )}
          {working && props.hasImageColumns && (
            <Tooltip content="Download the images of the current rows as a ZIP. No checkpoint needed.">
              <Button
                size="sm"
                variant="outline"
                loading={props.imagesPending}
                aria-label={props.imagesPending ? "Downloading images" : undefined}
                onClick={props.onDownloadImages}
              >
                <Images className="h-4 w-4" aria-hidden />
                <span className={labels.images}>Download images</span>
              </Button>
            </Tooltip>
          )}
          {working && (
            <DatasetShareButton
              datasetId={dataset.id}
              datasetName={dataset.name}
              labelClassName={labels.share}
            />
          )}
          {props.onNewMonitor && (
            <Tooltip content="Add new rows or update values on a schedule.">
              <Button
                size="sm"
                variant="outline"
                aria-label="New monitor"
                onClick={props.onNewMonitor}
              >
                <CalendarClock className="h-4 w-4" aria-hidden />
                <span className={labels.monitor}>Monitor</span>
              </Button>
            </Tooltip>
          )}
          <DropdownMenu modal={false}>
            <Tooltip content="More actions">
              <DropdownMenuTrigger asChild>
                <Button
                  ref={moreRef}
                  size="icon"
                  variant="outline"
                  loading={props.deleting}
                  aria-label="More actions"
                >
                  <Ellipsis className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-60">
              {working && (
                <>
                  {dataset.status === "active" && (
                    <DropdownMenuItem className="gap-2" onSelect={() => setPanel("add-column")}>
                      <Plus className="h-4 w-4" aria-hidden />
                      Add column
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem className="gap-2" onSelect={() => setPanel("checkpoints")}>
                    <History className="h-4 w-4" aria-hidden />
                    <span className="flex-1">Checkpoints</span>
                    <span className="tabular-nums text-zinc-400">
                      {checkpointCount}/{MAX_CHECKPOINTS}
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2" onSelect={() => setPanel("plan")}>
                    <ListChecks className="h-4 w-4" aria-hidden />
                    Collection plan
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2" onSelect={() => setPanel("activity")}>
                    <Activity className="h-4 w-4" aria-hidden />
                    <span className="flex-1">Recent activity</span>
                    <span className="tabular-nums text-zinc-400">{activityCount}</span>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem asChild className="gap-2">
                <Link to={`/repos/${encodeURIComponent(dataset.repo_id)}`}>
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  View evidence repository
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="gap-2 text-red-700 focus:bg-red-50 focus:text-red-800"
                onSelect={props.onDelete}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete dataset
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <GoalAndRows dataset={dataset} plan={props.plan} />

      <Dialog open={panel === "plan"} onOpenChange={closePanel}>
        <DialogContent className="max-w-2xl" onCloseAutoFocus={returnFocus}>
          <DialogHeader>
            <DialogTitle>Collection plan</DialogTitle>
            <DialogDescription>
              The agent’s internal source and collection approach. It does not
              require approval.
            </DialogDescription>
          </DialogHeader>
          <PlanDetails plan={props.plan} />
        </DialogContent>
      </Dialog>
      <Dialog open={panel === "activity"} onOpenChange={closePanel}>
        <DialogContent onCloseAutoFocus={returnFocus}>
          <DialogHeader>
            <DialogTitle>Recent activity</DialogTitle>
            <DialogDescription>The latest changes to this dataset.</DialogDescription>
          </DialogHeader>
          <ActivityList activity={props.activity ?? []} />
        </DialogContent>
      </Dialog>
      <Dialog open={panel === "checkpoints"} onOpenChange={closePanel}>
        <DialogContent className="max-w-2xl" onCloseAutoFocus={returnFocus}>
          <DialogHeader>
            <DialogTitle>Saved checkpoints</DialogTitle>
            <DialogDescription>
              Restorable dataset versions. The latest {MAX_CHECKPOINTS} are
              retained.
            </DialogDescription>
          </DialogHeader>
          <CheckpointList {...props} />
        </DialogContent>
      </Dialog>
      <AddColumnDialog
        dataset={dataset}
        open={panel === "add-column"}
        agentWorking={props.agentWorking}
        onClose={() => setPanel(null)}
        onCloseAutoFocus={returnFocus}
      />
    </div>
  );
};

const text = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

/** The paragraph that the agent writes at the end: what it did and why. */
const recordedMethodology = (plan: ExtractionCollectionPlan | null | undefined) => {
  const value = plan?.plan.methodology;
  if (typeof value === "string") return value.trim();
  if (!Array.isArray(value)) return "";
  return value
    .map((step) => {
      if (typeof step !== "object" || step == null) return text(step);
      const item = step as Record<string, unknown>;
      return [text(item.step), text(item.detail)].filter(Boolean).join(": ");
    })
    .filter(Boolean)
    .map((step) => (/[.!?]$/.test(step) ? step : `${step}.`))
    .join(" ");
};

/** The plan in one or two sentences: its summary, else the sources of its passes. */
const planSummary = (plan: ExtractionCollectionPlan | null | undefined) => {
  const summary = plan?.summary?.trim();
  if (summary) return summary;
  const passes = Array.isArray(plan?.plan.passes) ? plan.plan.passes : [];
  const sources = [
    ...new Set(
      passes.map((pass) =>
        typeof pass === "object" && pass != null
          ? text((pass as Record<string, unknown>).source)
          : "",
      ),
    ),
  ].filter(Boolean);
  return sources.length > 0 ? `Collect the values from ${sources.join("; ")}.` : "";
};

const MENTIONS_MAP = /\bmap(s|ped|ping)?\b/i;

const METHOD_HEADING =
  "border-b border-zinc-200 pb-1 text-sm font-semibold text-zinc-900";

/** How the data was found, collapsed under the goal: the plan, then what the agent did. */
const Methodology = ({ plan }: { plan: ExtractionCollectionPlan | null | undefined }) => {
  const [open, setOpen] = useState(false);
  const summary = planSummary(plan);
  const done = recordedMethodology(plan);
  if (!summary && !done) return null;
  return (
    <section aria-labelledby="curation-methodology" className="mt-2">
      <h3 id="curation-methodology" className="text-sm">
        <button
          type="button"
          aria-expanded={open}
          aria-controls="curation-methodology-body"
          onClick={() => setOpen((current) => !current)}
          className="inline-flex items-center gap-1 rounded-[3px] font-medium text-zinc-700 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
        >
          <ChevronRight
            className={`h-4 w-4 motion-safe:transition-transform ${open ? "rotate-90" : ""}`}
            aria-hidden
          />
          Methodology
        </button>
      </h3>
      {open && (
        <div
          id="curation-methodology-body"
          className="mt-2 space-y-4 rounded-[3px] border border-zinc-200 bg-zinc-50 px-4 py-3"
        >
          {summary && (
            <div className="space-y-2">
              <h4 className={METHOD_HEADING}>Plan</h4>
              <p className="text-sm leading-6 text-zinc-700">{summary}</p>
              {MENTIONS_MAP.test(summary) && (
                <p className="text-xs leading-5 text-zinc-500">
                  To map papers, a model reads each paper in full and copies the value of each
                  column into the table, with the passage that supports it.
                </p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <h4 className={METHOD_HEADING}>What the agent did</h4>
            {done ? (
              <p className="whitespace-pre-wrap text-sm leading-6 text-zinc-700">{done}</p>
            ) : (
              <p className="text-sm leading-6 text-zinc-500">
                The agent writes this at the end of collection: what it searched, how many papers
                each search found, and why it chose the papers that it read.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

/** The goal and row definition across the full width, one line until expanded. */
const GoalAndRows = ({
  dataset,
  plan,
}: {
  dataset: ExtractionDataset;
  plan: ExtractionCollectionPlan | null | undefined;
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element || expanded) return;
    const measure = () =>
      setOverflowing(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [dataset.goal, dataset.row_definition, expanded]);

  return (
    <div className="mt-1 flex items-start gap-2">
      <div id="curation-goal" className="min-w-0 flex-1">
        <p
          ref={textRef}
          className={`text-sm leading-6 text-zinc-600 ${
            expanded ? "whitespace-pre-wrap" : "line-clamp-1"
          }`}
        >
          <span className="font-medium text-zinc-500">Goal:</span> {dataset.goal}
          <span className="text-zinc-400"> · </span>
          <span className="font-medium text-zinc-500">Rows:</span>{" "}
          {dataset.row_definition}
        </p>
        <Methodology plan={plan} />
        <AgentPromptBox key={dataset.id} dataset={dataset} />
      </div>
      {(overflowing || expanded) && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls="curation-goal"
          onClick={() => setExpanded((current) => !current)}
          className="shrink-0 rounded-[3px] text-sm font-medium leading-6 text-zinc-700 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

const PlanDetails = ({ plan }: { plan: ExtractionCollectionPlan | null | undefined }) =>
  plan ? (
    <div className="space-y-2">
      {plan.summary && <p className="text-sm text-zinc-700">{plan.summary}</p>}
      <p className="text-xs font-medium text-zinc-600">Plan revision {plan.revision}</p>
      <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-words rounded-[3px] bg-zinc-50 p-3 text-xs text-zinc-700">
        {JSON.stringify(plan.plan, null, 2)}
      </pre>
    </div>
  ) : (
    <p className="text-sm text-zinc-500">
      The agent has not recorded a collection plan yet.
    </p>
  );

const ActivityList = ({ activity }: { activity: ExtractionGridActivity[] }) =>
  activity.length > 0 ? (
    <ol className="max-h-[60vh] space-y-3 overflow-y-auto">
      {activity
        .slice(-20)
        .reverse()
        .map((event) => (
          <li key={event.cursor} className="text-sm">
            <p className="font-medium text-zinc-700">{event.summary}</p>
            <p className="mt-0.5 text-xs text-zinc-400">{formatDate(event.created_at)}</p>
          </li>
        ))}
    </ol>
  ) : (
    <p className="text-sm text-zinc-500">No grid activity has been recorded yet.</p>
  );

const CheckpointList = (props: CurationHeaderProps) => {
  const checkpoints = props.checkpoints ?? [];
  if (props.checkpointsLoading) {
    return (
      <p className="text-sm text-zinc-500" role="status">
        Loading checkpoints…
      </p>
    );
  }
  if (props.checkpointsError) {
    return <p className="text-sm text-red-700">{props.checkpointsError}</p>;
  }
  if (!checkpoints.length) {
    return (
      <p className="text-sm text-zinc-500">
        No checkpoints yet. Save one whenever you want a restorable CSV and
        image snapshot.
      </p>
    );
  }
  return (
    <ol className="max-h-[60vh] divide-y divide-zinc-200 overflow-y-auto">
      {checkpoints.map((checkpoint) => (
        <li
          key={checkpoint.id}
          className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-zinc-900">
                Version {checkpoint.sequence}
              </span>
              <Badge variant={checkpoint.status === "ready" ? "green" : "default"}>
                {checkpoint.status === "ready" ? "Ready" : "Working"}
              </Badge>
            </div>
            <p className="mt-1 truncate text-sm text-zinc-600">
              {checkpoint.message ?? "Saved dataset state"}
            </p>
            <p className="mt-1 text-xs text-zinc-400">
              {checkpoint.row_count} rows · {checkpoint.populated_cells} populated ·{" "}
              {checkpoint.missing_cells} empty · {formatDate(checkpoint.created_at)}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              size="sm"
              loading={props.checkpointCsvId === checkpoint.id}
              onClick={() => props.onDownloadCheckpointCsv(checkpoint)}
            >
              <Download className="h-3.5 w-3.5" aria-hidden />
              CSV
            </Button>
            {props.hasImageColumns && (
              <Button
                variant="outline"
                size="sm"
                loading={props.checkpointImagesId === checkpoint.id}
                onClick={() => props.onDownloadCheckpointImages(checkpoint)}
              >
                <Images className="h-3.5 w-3.5" aria-hidden />
                Images
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              loading={props.restoringCheckpointId === checkpoint.id}
              onClick={() => props.onRestoreCheckpoint(checkpoint)}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Restore
            </Button>
          </div>
        </li>
      ))}
    </ol>
  );
};
